// SPDX-License-Identifier: AGPL-3.0-or-later

import {failureCode, failureContext} from '@app/features/platform/utils/ResponseInspection';
import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import type {UserBoostSlotResponse} from '@fluxer/schema/src/domains/boost/BoostResponseSchemas';
import type {PaddleBoostSubscriptionState} from '@fluxer/schema/src/domains/premium/PaddleSubscriptionSchemas';

// The server's GUILD_BOOST_SLOT_COOLDOWN_MS.
const SLOT_COOLDOWN_MS = 7 * 86_400_000;

function liveAt(slot: UserBoostSlotResponse, now: number): boolean {
	return slot.expires_at == null || Date.parse(slot.expires_at) > now;
}

// A slot that came off a community waits 7 days before it boosts again.
function coolingAt(slot: UserBoostSlotResponse, now: number): boolean {
	return slot.cooldown_until != null && Date.parse(slot.cooldown_until) > now;
}

// Free slots that still run and can boost now, the bought one that ends first on top: it is the one to use before it
// runs out. Premium slots, which last as long as premium, come last.
export function freeBoostSlots(slots: ReadonlyArray<UserBoostSlotResponse>, now: number): Array<UserBoostSlotResponse> {
	const ends = (slot: UserBoostSlotResponse) => (slot.expires_at == null ? Infinity : Date.parse(slot.expires_at));
	return slots
		.filter((slot) => slot.guild_id === null && liveAt(slot, now) && !coolingAt(slot, now))
		.sort((a, b) => ends(a) - ends(b));
}

// With no slot free now: in how many days, rounded up, the first waiting slot can boost again.
export function boostCooldownDays(slots: ReadonlyArray<UserBoostSlotResponse>, now: number): number | null {
	if (freeBoostSlots(slots, now).length > 0) return null;
	const ends = slots
		.filter((slot) => slot.guild_id === null && liveAt(slot, now) && coolingAt(slot, now))
		.map((slot) => Date.parse(slot.cooldown_until!));
	return ends.length === 0 ? null : Math.ceil((Math.min(...ends) - now) / 86_400_000);
}

export function liveBoughtSlotCount(slots: ReadonlyArray<UserBoostSlotResponse>, now: number): number {
	return slots.filter((slot) => slot.expires_at != null && liveAt(slot, now)).length;
}

export interface BoostPurchaseInput {
	shopRuns: boolean;
	// The catalog has a boost (`boost` is not null).
	onSale: boolean;
	salesOpen: boolean;
	// Who sells to this viewer, Paddle or Payriff; null when nobody does.
	provider: string | null;
	purchaseDisabled: boolean;
	slots: ReadonlyArray<UserBoostSlotResponse>;
	limit: number;
	now: number;
}

// Offered once no free slot is left. The server also counts open orders against the limit and decides at the order.
export function boostPurchaseState(input: BoostPurchaseInput): 'hidden' | 'available' | 'limit_reached' {
	if (!input.shopRuns || !input.onSale || !input.salesOpen || input.provider === null || input.purchaseDisabled) {
		return 'hidden';
	}
	if (freeBoostSlots(input.slots, input.now).length > 0) return 'hidden';
	return liveBoughtSlotCount(input.slots, input.now) >= input.limit ? 'limit_reached' : 'available';
}

// Which dialog buying boosts opens: a new subscription, a change of the one there is, or one 30 day slot beside one
// that cannot change now (a cancellation, a failed payment, a review, or an end with paid time left).
export function boostSubscriptionRoute(
	subscription: PaddleBoostSubscriptionState | null,
	now: number,
): 'start' | 'change' | 'one_time' {
	if (!subscription) return 'start';
	if (subscription.can_change_quantity) return 'change';
	const paidAhead = subscription.paid_until !== null && Date.parse(subscription.paid_until) > now;
	return subscription.status === 'canceled' && !paidAhead ? 'start' : 'one_time';
}

// 404: the slot ended or was refunded. 409 GUILD_BOOST_SLOT_COOLDOWN: it was put on a community in the last 7 days.
// 409 CONFLICT: it is on another community or lost a race (an older API also answers the 7-day wait this way).
export function boostFailure(error: unknown): 'slot_gone' | 'slot_cooling' | 'slot_busy' | null {
	switch (failureCode(error)) {
		case APIErrorCodes.NOT_FOUND:
			return 'slot_gone';
		case APIErrorCodes.GUILD_BOOST_SLOT_COOLDOWN:
			return 'slot_cooling';
		case APIErrorCodes.CONFLICT:
			return 'slot_busy';
		default:
			return null;
	}
}

// When a slot the server refused as waiting can boost again, from the reply; null when the reply does not say.
export function boostCooldownUntil(error: unknown): number | null {
	const until = failureContext(error)?.cooldown_until;
	return typeof until === 'string' && !Number.isNaN(Date.parse(until)) ? Date.parse(until) : null;
}

// Taking a boost off frees its slot, which still waits 7 days from when it was put on the community: when that wait
// ends, or null when the slot list does not say when the slot was put on, or the bought slot ends by then or has ended.
export function removedSlotFreeAt(
	slots: ReadonlyArray<UserBoostSlotResponse>,
	boostId: string,
	now: number,
): number | null {
	const slot = slots.find((candidate) => candidate.boost_id === boostId);
	if (slot?.assigned_at == null) return null;
	const freeAt = Date.parse(slot.assigned_at) + SLOT_COOLDOWN_MS;
	const endsAt = slot.expires_at == null ? Infinity : Date.parse(slot.expires_at);
	return endsAt <= freeAt || endsAt <= now ? null : freeAt;
}
